import React, { useState, useEffect } from 'react';
import { TokenStorage } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';
import '../../styles/BillingPage.scss';
import { Download } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com/api';

declare global { interface Window { Razorpay: any; } }

function loadRazorpay(): Promise<boolean> {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

async function apiFetch(endpoint: string, method = 'GET', body?: object) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  return { ok: res.ok, data: await res.json() };
}

function PageHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="flex items-center gap-3 px-6 py-4"
      style={{ background: '#fff', borderBottom: '1px solid #e8edf4' }}>
      <div>
        <h1 className="text-[15px] font-black text-gray-900 tracking-tight leading-none">{title}</h1>
        <p className="text-[11px] text-gray-400 font-medium mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function PageLoading() {
  return (
    <div className="billing-spinner-wrap" style={{ background: '#f0f4f8', minHeight: 400 }}>
      <div className="billing-spinner" />
    </div>
  );
}

const LOGO_PATH = '/manna-plag-logo-horizontal.png?v=2';

const COMPANY_NAME = 'Manna Analytics Private Limited';
const COMPANY_ADDRESS = '3/343, Town Railway Nagar, Nagercoil 629001, Tamil Nadu, India';
const COMPANY_EMAIL = 'support@mannanalytics.com';
const DEFAULT_GST_PERCENT = 18;

function escapeHtml(value: any) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatMoney(currency: string, value: number) {
  return `${currency}${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export default function BillingPage() {
  const [sub, setSub] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/payments/my-subscription/')
      .then(({ data }) => setSub(data))
      .finally(() => setLoading(false));
  }, []);

  const handlePayNow = async () => {
    if (!sub) return;
    setPaying(true); setError('');
    try {
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error('Payment system failed to load.');

      const { ok, data: orderData } = await apiFetch('/payments/create-order/', 'POST', {
        plan_id: sub.plan_id,
        currency_id: sub.currency_id || 1,
      });
      if (!ok) throw new Error(orderData.error || 'Could not create order.');

      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Manna Plag',
        description: `${orderData.plan_name} — Renewal`,
        order_id: orderData.order_id,
        prefill: orderData.prefill,
        theme: { color: '#003c6c' },
        handler: async (response: any) => {
          const { ok: vOk, data: vData } = await apiFetch('/payments/verify/', 'POST', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            subscription_id: orderData.subscription_id,
          });
          if (vOk) {
            window.location.reload();
          } else {
            setError(vData.error || 'Verification failed.');
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      new window.Razorpay(options).open();
    } catch (e: any) {
      setError(e.message);
      setPaying(false);
    }
  };

  const downloadInvoice = (payment: any) => {
    const w = window.open('', '_blank');
    if (!w) return;

    const currency = payment.currency || '';
    const taxable = payment.plan_amount ?? payment.total_amount;
    const gst = payment.gst_amount ?? 0;
    const total = payment.total_amount;
    const statusLabel = (payment.status === 'paid' || payment.status === 'success') ? 'Paid'
      : String(payment.status || 'Paid').replace(/^\w/, (c: string) => c.toUpperCase());
    const invoiceNumber = payment.invoice_number ?? payment.razorpay_payment_id;
    const logoUrl = `${window.location.origin}${LOGO_PATH}`;

    w.document.write(`
      <html><head><title>Invoice — ${escapeHtml(invoiceNumber)}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: sans-serif; padding: 40px; color: #111; max-width: 760px; margin: 0 auto; font-size: 13px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
        .header img { height: 42px; width: auto; display: block; }
        .company-name { font-weight: 800; font-size: 14px; margin: 10px 0 4px; }
        .company { color: #666; font-size: 11.5px; line-height: 1.6; }
        .doc-title { font-weight: 800; font-size: 18px; letter-spacing: .02em; }
        .badge { background: #f0fdf4; color: #16a34a; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; display: inline-block; margin-top: 8px; }

        .meta-table { width: 100%; border-collapse: collapse; margin: 20px 0; border: 1px solid #e5e7eb; }
        .meta-table td { padding: 10px 12px; border: 1px solid #e5e7eb; vertical-align: top; }
        .meta-label { font-size: 9.5px; color: #999; text-transform: uppercase; letter-spacing: .04em; font-weight: 700; display: block; margin-bottom: 3px; }
        .meta-value { font-weight: 700; color: #111; font-size: 12.5px; }
        .meta-value.mono { font-family: monospace; font-size: 11.5px; font-weight: 600; }

        .items { width: 100%; border-collapse: collapse; margin-top: 24px; border: 1px solid #e5e7eb; }
        .items th { background: #f9fafb; font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: .04em; text-align: left; padding: 10px 12px; border: 1px solid #e5e7eb; }
        .items td { padding: 12px; border: 1px solid #e5e7eb; }
        .items th.amt, .items td.amt { text-align: right; }
        .items .sub-label td { color: #666; }
        .items .grand-row td { font-weight: 900; font-size: 14.5px; background: #f9fafb; }

        .footer-note { margin-top: 28px; color: #999; font-size: 10.5px; line-height: 1.6; border-top: 1px solid #eee; padding-top: 16px; }
      </style>
      </head><body>
        <div class="header">
          <div>
            <img src="${logoUrl}" alt="Manna Plag" onerror="this.style.display='none'" />
            <p class="company-name">${escapeHtml(COMPANY_NAME)}</p>
            <p class="company">
              ${escapeHtml(COMPANY_ADDRESS)}<br/>
              ${escapeHtml(COMPANY_EMAIL)}
            </p>
          </div>
          <div style="text-align:right">
            <p class="doc-title">TAX INVOICE</p>
            <p style="color:#666;font-size:12.5px;margin-top:4px">${escapeHtml(payment.date)}</p>
            <span class="badge">${escapeHtml(statusLabel)}</span>
          </div>
        </div>

        <table class="meta-table">
          <tr>
            <td style="width:34%">
              <span class="meta-label">Invoice Number</span>
              <span class="meta-value mono">${escapeHtml(invoiceNumber)}</span>
            </td>
            <td style="width:33%">
              <span class="meta-label">Payment ID</span>
              <span class="meta-value mono">${escapeHtml(payment.razorpay_payment_id)}</span>
            </td>
            <td style="width:33%">
              <span class="meta-label">Order ID</span>
              <span class="meta-value mono">${escapeHtml(payment.razorpay_order_id)}</span>
            </td>
          </tr>
        </table>

        <table class="items">
          <thead>
            <tr>
              <th style="width:6%">#</th>
              <th>Description</th>
              <th class="amt" style="width:20%">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>${escapeHtml(payment.plan_name)} Plan — Subscription</td>
              <td class="amt">${escapeHtml(formatMoney(currency, taxable))}</td>
            </tr>
            <tr class="sub-label">
              <td></td>
              <td>Taxable Value</td>
              <td class="amt">${escapeHtml(formatMoney(currency, taxable))}</td>
            </tr>
            <tr class="sub-label">
              <td></td>
              <td>GST${gst ? ` (${DEFAULT_GST_PERCENT}%)` : ''}</td>
              <td class="amt">${escapeHtml(formatMoney(currency, gst))}</td>
            </tr>
            <tr class="grand-row">
              <td></td>
              <td>Total Amount</td>
              <td class="amt">${escapeHtml(formatMoney(currency, total))}</td>
            </tr>
          </tbody>
        </table>

        <p class="footer-note">
          This is a system-generated invoice and does not require a signature.<br/>
          For queries regarding this invoice, contact ${escapeHtml(COMPANY_EMAIL)}.
        </p>

        <script>
          var img = document.querySelector('.header img');
          if (img && img.complete) { window.print(); }
          else if (img) { img.addEventListener('load', function(){ window.print(); }); img.addEventListener('error', function(){ window.print(); }); }
          else { window.print(); }
        </script>
      </body></html>
    `);
    w.document.close();
  };

  if (loading) return <PageLoading />;

  const payments: any[] = sub?.payments || [];
  const upcoming = sub?.upcoming_invoice;

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: '#f0f4f8' }}>
      <PageHeader title="Billing" sub="Transaction history and invoices" />

      <div className="p-6 max-w-6xl space-y-4">

        {error && (
          <div className="p-3 rounded-xl text-[12px] font-medium"
            style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
            {error}
          </div>
        )}

        {/* Upcoming invoice */}
        {upcoming && (
          <div className="billing-upcoming">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <i className="ti ti-clock text-amber-500 text-[15px]" />
                  <p className="text-[12px] font-black text-amber-800">Upcoming Invoice</p>
                </div>
                <p className="text-[13px] font-semibold text-amber-700">
                  {upcoming.currency}{upcoming.amount} due {upcoming.due_date}
                </p>
                <p className="text-[11px] text-amber-600 font-medium mt-0.5">
                  {upcoming.days_remaining === 0 ? 'Due today' : `${upcoming.days_remaining} days remaining`}
                </p>
              </div>
              <button onClick={handlePayNow} disabled={paying}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-[12px] font-black disabled:opacity-60 transition-opacity hover:opacity-90"
                style={{ background: '#003c6c' }}>
                <i className="ti ti-credit-card text-[13px]" />
                {paying ? 'Processing...' : 'Pay now'}
              </button>
            </div>
          </div>
        )}

        {/* Transaction history — same card/table/row language as Team Management */}
        <div className="billing-card">
          <div className="billing-card__header">
            <p className="billing-card__title">
              Transaction History {payments.length > 0 && <span className="count">({payments.length})</span>}
            </p>
          </div>

          {payments.length === 0 ? (
            <div className="billing-empty-state">
             <FontAwesomeIcon icon={faReceipt} className="billing-empty-icon" />
              <p>No transactions yet</p>
              <p>Your invoices will appear here after your first payment.</p>
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div className="billing-table-header">
                <span>Description</span>
                <span>Date</span>
                <span className="align-right">Amount</span>
                  {/* <span className="align-right">payment Method</span> */}
                <span className="align-center">Status</span>
                <span className="align-center">Invoice</span>
              </div>

              {payments.map((p: any) => {
                const status = (p.status || 'paid').toLowerCase();
                return (
                  <div key={p.id} className="billing-row">
                    <div>
                      <p className="billing-row__desc">{p.plan_name} Plan</p>
                      <p className="billing-row__id">{p.razorpay_payment_id}</p>
                    </div>
                    <span className="billing-row__date">{p.date}</span>
                    <span className="billing-row__amount">{p.currency}{p.total_amount}</span>
                          {/* <span className="billing-row__amount">{p.payment_method}</span> */}
                    <div style={{ textAlign: 'center' }}>
                      <span className={`billing-badge billing-badge--${status === 'paid' || status === 'success' ? 'paid' : status === 'pending' ? 'pending' : 'failed'}`}>
                        {status === 'paid' || status === 'success' ? 'Paid' : status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                    <div className="billing-actions">
                      <button onClick={() => downloadInvoice(p)} className="billing-icon-btn" title="Download invoice">
                        <Download size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}